import React, { useEffect, useState } from 'react';
import './DashboardCard.css';
import './MyDocumentsCard.css';
import '../primitives/Button.css';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext'; // ✅ NEW: Use DataContext for document types + SWR cache
import { Link } from 'react-router-dom'; // removed useNavigate
import { EndeavorApi } from '../../services/ApiService';
import DocumentCourseAssignModal from './DocumentCourseAssignModal';
import { getAudioDocumentTypeIds } from '../../utils/documentTypeUtils';
import ThemedSelect from '../primitives/ThemedSelect';

const getDate = d =>
  d.createdAt || d.uploadedAt || d.created_at || d.upload_date || d.date_uploaded || d.dateUploaded || d.timestamp || d.date;

/**
 * Props:
 *   refreshKey
 *   maxItems (display cap, default 100)
 */
export default function MyDocumentsCard({
  refreshKey,
  maxItems = 100,
  endeavorId: scopedEndeavorId, // if provided = course scoped mode

  // NEW: Selection mode props
  selectionMode = false,
  selectedDocumentIds = [],
  onToggleSelect,                // (docId:string|number) => void
  onSelectionChange,             // (ids:string[]) => void (optional)
  disableNavigationInSelection = true,

  // NEW: ScoutIQ mode props
  scoutIQMode = false,           // when true, use custom click handler instead of navigation
  onDocumentClick,               // (docId, docName) => void - called when document is clicked in ScoutIQ mode

  // NEW: filtering helpers when embedded (e.g., in modal)
  requiredStatusIds = [],        // e.g., [7] -> only show docs that have status 7
  pinnedDocumentTypeId = null    // if provided, force filter to this document type id
}) {
  const { user } = useAuth();
  const { documentTypes, getDocumentsByUserSWR, clearCache } = useData(); // ✅ NEW: Get cached document types + SWR from DataContext
  // const navigate = useNavigate(); // removed
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // filters / sort
  const [typeFilter, setTypeFilter] = useState(pinnedDocumentTypeId ? String(pinnedDocumentTypeId) : 'all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('date');               // default changed to 'date'
  const [sortDirection, setSortDirection] = useState('desc');       // most recent first
  const [courseFilter, setCourseFilter] = useState('all');          // endeavorId or 'all'
  const [endeavorMap, setEndeavorMap] = useState({});               // id -> name
  const [docTypes, setDocTypes] = useState([]);                     // document type options
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseModalDoc, setCourseModalDoc] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // default changed to 10
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [refetchNonce, setRefetchNonce] = useState(0);

  // ADD: retry helper (replaces missing loadDocuments)
  const triggerReload = () => setRefetchNonce(n => n + 1);

  const userId = user?.id || user?.userId;

  // Listen for external notifications that documents have changed (e.g., new upload elsewhere)
  // We use a window-level CustomEvent 'documents:changed' so any uploader can broadcast.
  useEffect(() => {
    const handler = (e) => {
      // Bust the SWR cache so the next fetch does a real network request
      if (clearCache) clearCache('documents_user');
      triggerReload(); // immediate attempt
      // Secondary delayed attempt to catch eventual consistency / indexing lag
      setTimeout(() => {
        triggerReload();
      }, 600);
    };
    window.addEventListener('documents:changed', handler);
    return () => window.removeEventListener('documents:changed', handler);
  }, [clearCache]);

  // --- EFFECT: fetch documents whenever any dependency changes ---
  useEffect(() => {
    if (!user) return;
    const abort = new AbortController();

    // Map UI sort field -> backend sortable field(s)
    const backendSortOrders = (() => {
      // Multi-column example: always add name asc as tiebreaker (optional)
      switch (sortField) {
        case 'name':
          return [`name,${sortDirection}`];
        case 'date':
          return [`createdAt,${sortDirection}`];
        case 'type':
          // documentTypeId is sortable; add name asc for stable grouping
          return [`documentTypeId,${sortDirection}`, 'name,asc'];
        case 'course':
          // NOT directly sortable server-side -> fall back to createdAt desc (stable)
          return [`createdAt,${sortDirection === 'asc' ? 'asc' : 'desc'}`];
        default:
          return [`createdAt,desc`];
      }
    })();

    const serverSort = backendSortOrders;

    const fetchDocs = async () => {
      console.log('[MyDocumentsCard] Starting fetch with endeavorId:', scopedEndeavorId, 'courseFilter:', courseFilter);
      setLoading(true);
      setErr(null);
      try {
        // Dynamically get audio document type IDs to exclude (types with names like "audio/*")
        // These are generated podcast/audiobook files managed by AI tools and should not 
        // appear in document lists.
        // ✅ Pass DataContext types to avoid redundant API call
        const audioTypeIds = await getAudioDocumentTypeIds(documentTypes);
        
        // Exclude audio types and notes (type 6) from main document list
        const excludeTypes = [...audioTypeIds, 6];
        
        const params = {
          page,
            size: pageSize,
          sort: serverSort,
          excludeDocumentTypeIds: excludeTypes
        };

        if (typeFilter !== 'all') params.documentTypeId = Number(typeFilter);
        if (scopedEndeavorId) {
          params.endeavorId = scopedEndeavorId;
        } else if (courseFilter !== 'all') {
          params.endeavorId = Number(courseFilter);
        }
        const trimmed = search.trim();
        if (trimmed.length > 1) params.name = trimmed;
        
        // NOTE: Status filtering removed to show ALL documents (including unprocessed).
        // UnifiedContentService now handles unprocessed documents transparently,
        // so we show them in the modal and mark as disabled/grayed if they lack required status.
        // Client-side marking happens below after fetching all documents.

        // Helper to process raw API data into component state
        const processData = (data) => {
          const list = Array.isArray(data)
            ? data
            : (data.content || data.documents || []);
          
          // Mark documents without required status as disabled instead of filtering them out
          const processedList = list.map(doc => {
            if (requiredStatusIds.length > 0) {
              const docStatusIds = (doc.statuses || []).map(s => s.id);
              const hasRequiredStatus = requiredStatusIds.some(reqId => docStatusIds.includes(reqId));
              return { ...doc, _isDisabled: !hasRequiredStatus };
            }
            return doc;
          });
          
          setDocs(processedList);
          setTotalPages(data.totalPages ?? 1);
          setTotalElements(data.totalElements ?? list.length);
          
          console.log('[MyDocumentsCard] Successfully loaded', processedList.length, 'documents for endeavorId:', scopedEndeavorId);

          // discover doc types
          const discovered = [];
          const seen = new Set();
          list.forEach(d => {
            const id = d.documentTypeId || d.docTypeId;
            const nm = d.documentTypeName || d.documentType || d.typeName;
            if (id && !seen.has(id)) { seen.add(id); discovered.push({ id, name: nm || `Type ${id}` }); }
          });
          discovered.sort((a,b)=>(a.name||'').localeCompare(b.name||''));
          setDocTypes(prev => {
            const map = new Map(prev.map(p => [p.id, p]));
            discovered.forEach(t => { if (!map.has(t.id)) map.set(t.id, t); });
            return [...map.values()].sort((a,b)=>(a.name||'').localeCompare(b.name||''));
          });
        };

        // Use SWR: returns cached data instantly, refreshes in background
        const data = await getDocumentsByUserSWR(
          userId,
          params,
          // onUpdate: silently replace list when fresh data arrives
          (freshData) => processData(freshData)
        );
        processData(data);
      } catch (e) {
        // Don't show errors for aborted requests - just silently clear loading
        if (e.name === 'AbortError' || e.message?.includes('aborted')) {
          console.log('[MyDocumentsCard] Fetch aborted, clearing loading state');
        } else {
          console.error('[MyDocumentsCard] Fetch error:', e);
          setErr(e);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDocs();
    return () => abort.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user, userId,
    page, pageSize,
    sortField, sortDirection,
    typeFilter, courseFilter, scopedEndeavorId,
    search,
    refreshKey,
    refetchNonce
  ]);

  // --- RESET PAGE when certain filters change (exclude page itself) ---
  useEffect(() => {
    setPage(0);
  }, [typeFilter, courseFilter, scopedEndeavorId]);

  // Load endeavor names (only when not scoped OR still need names for display)
  useEffect(() => {
    const collect = async () => {
      const ids = new Set();
      docs.forEach(d => {
        (d.endeavorIds || []).forEach(id => ids.add(id));
      });
      if (ids.size === 0) return;
      const missing = [...ids].filter(id => !endeavorMap[id]);
      if (missing.length === 0) return;
      try {
        const entries = await Promise.all(missing.map(async id => {
          try {
            const res = await EndeavorApi.getEndeavor(id);
            if (res.ok) {
              const data = await res.json();
              return [id, data.name || data.title || `Course ${id}`];
            }
          } catch {}
          return [id, `Course ${id}`];
        }));
        setEndeavorMap(prev => {
          const next = { ...prev };
            entries.forEach(([i, n]) => { next[i] = n; });
          return next;
        });
      } finally {
      }
    };
    collect();
  }, [docs, endeavorMap]);

  // (Optional) debounce search -> simple immediate reset
  useEffect(() => {
    const t = setTimeout(() => setPage(0), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Apply client-side filters EXCEPT we now keep unprocessed docs (they will appear disabled).
  // We still apply type/course/search filters.
  const filtered = docs.filter(d => {
    // Type filter already applied server-side but re-check pinned type (defensive)
    if (pinnedDocumentTypeId && (d.documentTypeId || d.docTypeId) !== Number(pinnedDocumentTypeId)) return false;
    if (search && search.trim().length > 1) {
      if (!d.name?.toLowerCase().includes(search.toLowerCase())) return false;
    }
    return true;
  });

  // Determine processing status for each doc (processed if has all requiredStatusIds).
  const isDocProcessed = (doc) => {
    if (!Array.isArray(requiredStatusIds) || requiredStatusIds.length === 0) return true; // no gating -> treat all as processed
    const statuses = doc.statuses || [];
    const statusIds = statuses.map(s => s.id || s.statusId).filter(Boolean);
    return requiredStatusIds.every(req => statusIds.includes(Number(req)));
  };

  const clientSorted = (() => {
    if (sortField !== 'course') return filtered; // server handled others
    // Client sort by primary (first) course name string
    return [...filtered].sort((a,b)=>{
      const aName = ((a.endeavorIds||[]).map(id=>endeavorMap[id]).filter(Boolean)[0]||'').toLowerCase();
      const bName = ((b.endeavorIds||[]).map(id=>endeavorMap[id]).filter(Boolean)[0]||'').toLowerCase();
      if (aName < bName) return sortDirection === 'asc' ? -1 : 1;
      if (aName > bName) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  // Helper: select all visible (only when selectionMode)
  const visibleIds = clientSorted
    .map(d => (d.documentId || d.id))
    .filter(Boolean)
    .map(String);

  const allVisibleSelected = selectionMode
    ? (visibleIds.length > 0 && visibleIds.every(id => selectedDocumentIds.includes(String(id))))
    : false;

  const toggleSelectAllVisible = () => {
    if (!selectionMode) return;
    if (allVisibleSelected) {
      const next = selectedDocumentIds.filter(id => !visibleIds.includes(String(id)));
      onSelectionChange ? onSelectionChange(next) : visibleIds.forEach(id => onToggleSelect?.(id));
    } else {
      const set = new Set(selectedDocumentIds.map(String));
      visibleIds.forEach(id => set.add(String(id)));
      onSelectionChange ? onSelectionChange([...set]) : visibleIds.forEach(id => onToggleSelect?.(id));
    }
  };

  const total = docs.length;

  const openCourseModal = (doc) => {
    setCourseModalDoc(doc);
    setCourseModalOpen(true);
  };
  const handleCourseModalClose = () => {
    setCourseModalOpen(false);
    setCourseModalDoc(null);
  };
  const handleCourseAssignmentsUpdated = (documentId, newEndeavorIds) => {
    setDocs(prev => prev.map(d => {
      const id = d.documentId || d.id;
      if (id === documentId) {
        return { ...d, endeavorIds: newEndeavorIds };
      }
      return d;
    }));
    handleCourseModalClose();
  };

  return (
    <div className="dashboard-card my-documents-card merged-docs-card">
      {!selectionMode && (
        <div className="card-main-header">
          <div className="card-title-with-counts">
            <h2 className="card-main-title">
              My Documents
            </h2>
            <div className="card-counts">
              {loading
                ? 'Loading…'
                : `Showing ${clientSorted.length} of ${totalElements}`}
            </div>
          </div>
        </div>
      )}

      <div className="docs-controls-toolbar">
        {/* Left zone: Sort controls */}
        <div className="toolbar-zone toolbar-zone--left">
          <div className="segmented-control" role="tablist" aria-label="Sort field">
            {[
              { v: 'name', l: 'Name' },
              { v: 'date', l: 'Date' },
              { v: 'type', l: 'Type' },
              { v: 'course', l: 'Course' }
            ].map(opt => (
              <label key={opt.v} className="segment">
                <input
                  type="radio"
                  value={opt.v}
                  checked={sortField === opt.v}
                  onChange={(e) => setSortField(e.target.value)}
                  aria-label={`Sort by ${opt.l}`}
                />
                <span className={`segment-text ${sortField === opt.v ? 'active' : ''}`}>{opt.l}</span>
              </label>
            ))}
          </div>

          <button
            type="button"
            className="direction-toggle"
            onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
            aria-label={`Sort ${sortDirection === 'asc' ? 'ascending' : 'descending'} — click to flip`}
            title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortDirection === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        {/* Center zone: Filters + Select Visible */}
        <div className="toolbar-zone toolbar-zone--center">
          {!scopedEndeavorId && (
            <ThemedSelect
              value={courseFilter}
              onChange={val => setCourseFilter(val)}
              options={[
                { value: 'all', label: 'All Courses' },
                ...Object.entries(endeavorMap)
                  .sort((a,b)=>a[1].localeCompare(b[1]))
                  .map(([id,name])=>({ value: id, label: name }))
              ]}
              ariaLabel="Course filter"
              searchable={Object.keys(endeavorMap).length > 5}
              className="filter-select"
            />
          )}

          <ThemedSelect
            value={typeFilter}
            onChange={val => setTypeFilter(val)}
            options={[
              { value: 'all', label: 'All Types' },
              ...docTypes.map(dt => ({ value: String(dt.id), label: dt.name }))
            ]}
            ariaLabel="Type filter"
            disabled={!!pinnedDocumentTypeId}
            className="filter-select"
          />

          {selectionMode && (
            <button
              type="button"
              className={`toolbar-select-btn ${allVisibleSelected ? 'toolbar-select-btn--active' : ''}`}
              onClick={toggleSelectAllVisible}
              disabled={loading || clientSorted.length === 0}
              title={allVisibleSelected ? 'Deselect all visible' : 'Select all visible documents'}
            >
              <svg className="toolbar-select-icon" viewBox="0 0 16 16" fill="none" width="14" height="14">
                {allVisibleSelected
                  ? <><rect x="1.5" y="1.5" width="13" height="13" rx="3" fill="currentColor" stroke="currentColor" strokeWidth="1.2"/><path d="M4.5 8.2L7 10.5L11.5 5.5" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></>
                  : <rect x="1.5" y="1.5" width="13" height="13" rx="3" stroke="currentColor" strokeWidth="1.2"/>
                }
              </svg>
              <span>{allVisibleSelected ? 'Deselect All' : 'Select Visible'}</span>
            </button>
          )}
        </div>

        {/* Right zone: Search */}
        <div className="toolbar-zone toolbar-zone--right">
          <div className="docs-search-wrap">
            <svg className="docs-search-ico" viewBox="0 0 24 24" stroke="currentColor" fill="none"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              className="docs-search-field"
              placeholder="Search documents…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              aria-label="Search documents"
            />
          </div>
        </div>
      </div>

      {/* Active Filter Chips */}
      {(search || courseFilter !== 'all' || typeFilter !== 'all') && (
        <div className="filter-chips">
          {search && (
            <div className="filter-chip">
              <span className="chip-label">Search: "{search}"</span>
              <button
                className="chip-remove"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                ×
              </button>
            </div>
          )}
          {courseFilter !== 'all' && !scopedEndeavorId && (
            <div className="filter-chip">
              <span className="chip-label">Course: {endeavorMap[courseFilter] || courseFilter}</span>
              <button
                className="chip-remove"
                onClick={() => setCourseFilter('all')}
                aria-label="Clear course filter"
              >
                ×
              </button>
            </div>
          )}
          {typeFilter !== 'all' && !pinnedDocumentTypeId && (
            <div className="filter-chip">
              <span className="chip-label">Type: {docTypes.find(dt => dt.id === Number(typeFilter))?.name || typeFilter}</span>
              <button
                className="chip-remove"
                onClick={() => setTypeFilter('all')}
                aria-label="Clear type filter"
              >
                ×
              </button>
            </div>
          )}
          <button
            className="clear-all-filters"
            onClick={() => {
              setSearch('');
              setCourseFilter('all');
              if (!pinnedDocumentTypeId) setTypeFilter('all');
            }}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Selection mode: course-specific bulk select */}
      {selectionMode && courseFilter !== 'all' && !scopedEndeavorId && (
        <div className="selection-buttons-toolbar" style={{ padding: '0.5rem 1rem', display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
            <button
              type="button"
              className="ui-btn ui-btn--neutral"
              onClick={() => {
                const courseDocIds = clientSorted.map(d => String(d.documentId || d.id));
                courseDocIds.forEach(id => {
                  if (!selectedDocumentIds.includes(id)) {
                    onToggleSelect?.(id);
                  }
                });
              }}
              disabled={loading || clientSorted.length === 0}
              title={`Select all documents from ${endeavorMap[courseFilter]}`}
            >
              ☐ Select All from {(endeavorMap[courseFilter] || '').length > 30 ? `${(endeavorMap[courseFilter] || '').substring(0, 30)}...` : endeavorMap[courseFilter]}
            </button>
        </div>
      )}

      <div className="card-content merged-docs-content">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <p>Loading documents...</p>
          </div>
        ) : err ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <p>{err.message || 'Failed to load documents.'}</p>
            <button
              className="ui-btn ui-btn--neutral"
              onClick={triggerReload}
              disabled={loading}
            >🔄 Retry</button>
          </div>
        ) : clientSorted.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              {total === 0 ? '📤' : (search || courseFilter !== 'all' || typeFilter !== 'all') ? '🔍' : '📄'}
            </div>
            <p className="empty-title">
              {total === 0 
                ? 'No documents yet' 
                : (search || courseFilter !== 'all' || typeFilter !== 'all')
                  ? 'No matches found'
                  : 'No documents available'}
            </p>
            <p className="empty-subtitle">
              {total === 0 
                ? 'Upload your first document to get started with DocuHounds AI analysis and insights.'
                : (search || courseFilter !== 'all' || typeFilter !== 'all')
                  ? 'Try adjusting your filters or search terms to find what you\'re looking for.'
                  : 'Documents will appear here once they are uploaded and processed.'}
            </p>
            {total === 0 && (
              <button
                className="ui-btn ui-btn--primary empty-action-btn"
                onClick={() => {
                  // Focus on upload area if present
                  const uploadBtn = document.querySelector('.upload-btn, .select-files-btn');
                  if (uploadBtn) {
                    uploadBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setTimeout(() => uploadBtn.focus(), 300);
                  }
                }}
              >
                📤 Go to Upload
              </button>
            )}
            {(search || courseFilter !== 'all' || typeFilter !== 'all') && (
              <button
                className="ui-btn ui-btn--neutral empty-action-btn"
                onClick={() => {
                  setSearch('');
                  setCourseFilter('all');
                  if (!pinnedDocumentTypeId) setTypeFilter('all');
                }}
              >
                Clear All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="documents-table-wrapper">
              <table className="documents-mini-table" role="table" aria-label="My documents">
                <thead>
                  <tr>
                    {selectionMode && <th className="select-col">&nbsp;</th>}
                    <th className="name-col">Name</th>
                    <th className="type-col">Type</th>
                    <th className="course-col">Course</th>
                    <th className="date-col">Date Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {clientSorted.map((d, i) => {
                    const idRaw = d.documentId || d.id;
                    const id = idRaw != null ? String(idRaw) : String(i);
                    const typeName = d.documentTypeName || d.documentType || d.typeName || '—';
                    const allCourseNames = (d.endeavorIds || [])
                      .map(eid => endeavorMap[eid])
                      .filter(Boolean);
                    const primaryCourseName = allCourseNames[0];
                    const extraCourses = allCourseNames.length > 1 ? allCourseNames.length - 1 : 0;
                    const dateVal = getDate(d);
                    const dateObj = dateVal ? new Date(dateVal) : null;
                    const dateStr = dateObj && !isNaN(dateObj) ? dateObj.toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric'
                    }) : '—';
                    const checked = selectedDocumentIds?.includes(id);
                    const processed = isDocProcessed(d);
                    const disabledForSelection = selectionMode && !processed; // can't select until processed
                    const isDisabled = d._isDisabled || false; // from status filtering
                    const tooltipMsg = !processed 
                      ? "We're indexing this document's content. It will be available for chat soon." 
                      : isDisabled 
                      ? "This document has not been processed for content generation"
                      : undefined;

                    return (
                      <tr key={id} className={`doc-row-line ${checked ? 'doc-row-selected' : ''} ${!processed ? 'doc-row-unprocessed' : ''} ${isDisabled ? 'doc-row-disabled' : ''}`} title={tooltipMsg}>
                        {selectionMode && (
                          <td className="select-col">
                            <input
                              type="checkbox"
                              className="ui-checkbox"
                              aria-label={`Select ${d.name || 'document'}`}
                              checked={!!checked}
                              disabled={disabledForSelection}
                              onChange={() => {
                                if (disabledForSelection) return; // block selection
                                onToggleSelect?.(id);
                              }}
                            />
                          </td>
                        )}
                        <td className="name-col">
                          <div className="name-cell">
                            <div className="text-col">
                              {selectionMode && disableNavigationInSelection ? (
                                <span className={`doc-link ${!processed ? 'unprocessed' : ''}`} style={{ cursor: 'default' }}>
                                  {d.name || 'Untitled'}
                                </span>
                              ) : scoutIQMode ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDocumentClick?.(id, d.name || 'Untitled');
                                  }}
                                  className={`doc-link doc-link-button ${!processed ? 'unprocessed' : ''}`}
                                  title={tooltipMsg || 'Click to preview document'}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    font: 'inherit',
                                    color: 'inherit',
                                    cursor: 'pointer',
                                    textAlign: 'left'
                                  }}
                                >
                                  {d.name || 'Untitled'}
                                </button>
                              ) : (
                                <Link
                                  to={`/document/${d.documentTypeId === 6 && d.parentDocumentId ? d.parentDocumentId : id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className={`doc-link ${!processed ? 'unprocessed' : ''}`}
                                  title={d.documentTypeId === 6 ? 'View parent document with this note' : tooltipMsg}
                                >
                                  {d.name || 'Untitled'}
                                </Link>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="type-col">
                          <span className={`type-badge ${!processed ? 'type-badge-unprocessed' : ''}`}>{typeName}</span>
                        </td>
                        <td className="course-col">
                          <div className="course-badge-container">
                            <button
                              type="button"
                              className={`course-badge-btn ${primaryCourseName ? '' : 'unassigned'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!selectionMode) {
                                  openCourseModal(d);
                                }
                              }}
                              disabled={selectionMode}
                              title={
                                primaryCourseName
                                  ? (extraCourses
                                      ? `${primaryCourseName} (+${extraCourses} more)`
                                      : primaryCourseName)
                                  : (selectionMode ? 'Course assignment disabled in selection' : 'Assign to a course')
                              }
                            >
                              {primaryCourseName
                                ? (extraCourses ? `${primaryCourseName} +${extraCourses}` : primaryCourseName)
                                : (selectionMode ? 'No Course' : 'Click to Assign')}
                            </button>
                          </div>
                        </td>
                        <td className="date-col">
                          <span className={`date-text ${!processed ? 'date-text-unprocessed' : ''}`}>{dateStr}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {clientSorted.length < totalElements && (
                <div className="showing-note-table">
                  Page {page + 1} of {totalPages || 1}
                </div>
              )}
            </div>

            <div className="docs-pagination">
              <div className="page-size">
                <span className="page-size-label">Rows</span>
                <ThemedSelect
                  value={pageSize}
                  onChange={val => { setPageSize(Number(val)); setPage(0); }}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 20, label: '20' },
                  ]}
                  ariaLabel="Rows per page"
                  minWidth={60}
                />
              </div>
              <div className="pager">
                <button
                  type="button"
                  className="pager-btn"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                >
                  ‹ Prev
                </button>
                <span className="pager-status">
                  {page + 1} <span className="pager-sep">/</span> {totalPages || 1}
                </span>
                <button
                  type="button"
                  className="pager-btn"
                  onClick={() => setPage(p => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages || loading}
                >
                  Next ›
                </button>
              </div>
              <div className="total-rows">
                {totalElements} total
              </div>
            </div>
          </>
        )}
      </div>

      {/* Keep course modal only when not in selection mode */}
      {!selectionMode && courseModalOpen && courseModalDoc && (
        <DocumentCourseAssignModal
          open={courseModalOpen}
          document={courseModalDoc}
          endeavorMap={endeavorMap}
          onClose={handleCourseModalClose}
          onSave={handleCourseAssignmentsUpdated}
        />
      )}
    </div>
  );
}